import { parse } from "query-string";
import React from "react";
import { MapStateToPropsParam } from "react-redux";

import FeedUntrustworthyUser from "@/mk2/apps/forum/containers/FeedUntrustworthyUser/FeedUntrustworthyUser";
import { feedUntrustworthyUserLoad } from "@/mk2/apps/forum/containers/FeedUntrustworthyUser/FeedUntrustworthyUser.actions";
import feedNewestReducer, {
    FeedState,
} from "@/mk2/apps/forum/containers/FeedUntrustworthyUser/FeedUntrustworthyUser.reducers";
import feedNewestSaga from "@/mk2/apps/forum/containers/FeedUntrustworthyUser/FeedUntrustworthyUser.sagas";
import {
    getFeedUntrustworthyUserPostsOfComments,
    getFeedUntrustworthyUserState,
} from "@/mk2/apps/forum/containers/FeedUntrustworthyUser/FeedUntrustworthyUser.selectors";
import { ForumMessageSchema, PostEntity } from "@/mk2/apps/forum/schemas";
import { breadcrumbsForum } from "@/mk2/apps/forum/structuredData";
import { forumUrl } from "@/mk2/apps/forum/urls";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import Page, {
    AnchorLeaderboardProps,
    BrandingProps,
    HalfpageProps,
    InterstitialProps,
    PageRouteProps,
} from "@/mk2/pages/Page";
import {
    AppState,
    getRequestBaseUrl,
    getRequestDeviceMobile,
    getRequestPermissions,
    getRequestUser,
} from "@/mk2/reducers";
import { LoadingState } from "@/mk2/reducers/loadingState";
import { CommentEntity, UserEntity, UserSchema } from "@/mk2/schemas";
import { getDenormalizedEntities, getDenormalizedEntity } from "@/mk2/selectors";
import { Store } from "@/mk2/store/configureStore";
import { SEO_DESCRIPTION_FORUM, SEO_TITLE_FORUM } from "@/mk/autogenerated/translation";
import { hasAds } from "@/mk/bazaar/common/userUtils";

interface OwnProps {}

interface StateProps {
    after: number;
    requestUser: UserEntity;
    requestUserPermissions: string[];
    comments: CommentEntity[];
    posts: PostEntity[];
    user: UserEntity;
    hasNext: number;
    hasPrevious: number;
    loadingState: LoadingState;
    errorMessage: string;
    baseUrl: string;
    isMobile: boolean;
}

interface DispatchProps {
    onLoad(username: string, after?: number);
}

type RouteProps = PageRouteProps<{
    username: string;
}>;
export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class FeedUntrustworthyUserPage extends Page<OwnProps, StateProps, DispatchProps> {
    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const parsed = parse(ownProps.location.search, { parseNumbers: true });
            const after = parsed.after as number;
            const username = ownProps.match.params.username;

            const feedState: FeedState = getFeedUntrustworthyUserState(state, username);
            const comments: CommentEntity[] = getDenormalizedEntities<CommentEntity>(
                state,
                ForumMessageSchema,
                feedState.ids,
            );
            const posts: PostEntity[] = getFeedUntrustworthyUserPostsOfComments(state, comments);

            return {
                after,
                requestUser: getRequestUser(state),
                requestUserPermissions: getRequestPermissions(state),
                comments,
                posts,
                user: getDenormalizedEntity<UserEntity>(state, UserSchema, feedState.user),
                hasNext: feedState.hasNext,
                hasPrevious: feedState.hasPrevious,
                loadingState: feedState.loadingState,
                errorMessage: feedState.errorMessage,
                baseUrl: getRequestBaseUrl(state),
                isMobile: getRequestDeviceMobile(state),
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onLoad: feedUntrustworthyUserLoad,
        };
    }

    public getMetaTitle(props: Props): string {
        return SEO_TITLE_FORUM;
    }

    public getMetaDescription(props: Props): string {
        return SEO_DESCRIPTION_FORUM;
    }

    public getHeaderTitle(props: Props): string {
        return props.match.params.username;
    }

    public getStructuredData(props: Props): object[] {
        return [breadcrumbsForum(props.baseUrl)];
    }

    public render(props: Props): false | JSX.Element {
        return <FeedUntrustworthyUser {...props} />;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return true;
    }

    public getDefaultBackPath(props: OwnProps & StateProps & DispatchProps): string | null {
        return forumUrl;
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        return {
            zone: "Forum Category - Halfpage Sticky (Desktop)",
        };
    }

    public getBrandingProps(props: Props): BrandingProps {
        return {
            zone: props.isMobile
                ? "Forum Category - Top 01 - Mobile"
                : "Forum Category - Branding (Desktop)",
        };
    }

    public getInterstitialProps(props: Props): InterstitialProps | null {
        return { zone: "Forum Category - Interstitial" };
    }

    public getAnchorLeaderboardProps(props: Props): AnchorLeaderboardProps {
        const { requestUser, isMobile, posts } = props;

        if (!isMobile || (requestUser && !hasAds(requestUser))) {
            return null;
        }

        if ((posts?.length || 0) < 10) {
            return null;
        }

        return {
            zone: "Forum Category - Anchor - Mobile",
        };
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer("containers.forum.forumFeedUntrustworthyUser", feedNewestReducer);
        store.injectSaga("containers.forum.forumFeedUntrustworthyUser", feedNewestSaga);
    }
}
